import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Clock, Laptop, GraduationCap, CheckCircle2, Users, Sparkles } from 'lucide-react';
import { getProgramBySlug } from '../data/programs';
import ProgramLeadForm from '../components/landing/ProgramLeadForm';
import ProgramLeadPopup from '../components/landing/ProgramLeadPopup';
import BrochureDownloadModal from '../components/landing/BrochureDownloadModal';
import WhatsAppWidget from '../components/WhatsAppWidget';
import DemoBanner from '../components/DemoBanner';
import { Reveal } from '../components/ui/reveal';
import { SpotlightCard } from '../components/ui/spotlight-card';
import { ShinyText } from '../components/ui/shiny-text';
import { CountUp } from '../components/ui/count-up';
import { SplitText } from '../components/ui/split-text';
import { ScrollProgressBar } from '../components/ui/scroll-progress';

const LEAD_POPUP_DELAY_MS = 5000;

// "5 Months" -> { num: 5, rest: " Months" } so the figure can be counted up.
function splitLeadingNumber(text) {
  const match = /^(\d+)(.*)$/.exec(String(text).trim());
  if (!match) return null;
  return { num: parseInt(match[1], 10), rest: match[2] };
}

export default function ProgramLandingPage() {
  const { slug } = useParams();
  const program = getProgramBySlug(slug);

  const [submitted, setSubmitted] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Reset when navigating between different program pages (React Router
  // keeps this component mounted across param changes, so without this the
  // "submitted" state from one course would leak into the next).
  useEffect(() => {
    setSubmitted(false);
    setLeadName('');
    setPopupOpen(false);
    const timer = setTimeout(() => setPopupOpen(true), LEAD_POPUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [slug]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!program) return <Navigate to="/" replace />;

  const Icon = program.icon;
  const durationParts = splitLeadingNumber(program.duration);

  const handleLeadSubmitted = (submittedForm) => {
    setLeadName(submittedForm?.name || '');
    setSubmitted(true);
  };

  return (
    <div data-testid="program-landing-page" className="min-h-screen bg-[#F7F9FC]">
      <ScrollProgressBar color={program.accent} />
      <DemoBanner />

      <header
        className={`sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b transition-shadow duration-300 ${
          scrolled ? 'shadow-[0_4px_24px_rgba(15,31,61,0.08)] border-[#E1E7EF]' : 'border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="A7 Globetech" className="w-8 h-8" />
            <span className="font-heading font-bold text-lg text-[#0056D2]">A7 Globetech</span>
          </Link>
          {!submitted && (
            <button
              onClick={() => setPopupOpen(true)}
              data-testid="program-nav-get-in-touch"
              className="font-body text-sm font-semibold text-white px-4 sm:px-5 py-2.5 rounded-xl bg-[#62CED3] hover:bg-[#48BFC5] hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 transition-all duration-200 whitespace-nowrap"
            >
              Get in Touch with us
            </button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-10 lg:py-14 bg-white dot-grid-bg">

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[3fr_2fr] gap-10 items-start">
          {/* Content column — 60% */}
          <div data-testid="program-content">
            <Reveal>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold mb-4"
                style={{ backgroundColor: program.bg, color: program.accent }}
              >
                <Icon className="w-4 h-4" />
                <ShinyText text={program.title} speed={2.8} />
              </div>
            </Reveal>

            <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-[#0F1F3D] leading-tight mb-4">
              <SplitText text={program.tagline} startDelay={0.1} delay={0.045} />
            </h1>

            <Reveal delay={0.15}>
              <p className="font-body text-base text-[#5A6A85] leading-relaxed mb-6 max-w-2xl">
                {program.description}
              </p>
            </Reveal>

            {/* Quick facts */}
            <div className="flex flex-wrap gap-4 mb-5">
              {[
                { icon: Clock, node: durationParts ? <CountUp end={durationParts.num} suffix={durationParts.rest} /> : program.duration },
                { icon: Laptop, node: program.mode },
                { icon: GraduationCap, node: program.level },
              ].map(({ icon: FactIcon, node }, i) => (
                <Reveal key={i} delay={0.2 + i * 0.08} y={16}>
                  <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-[#E1E7EF] hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
                    <FactIcon className="w-4 h-4" style={{ color: program.accent }} />
                    <span className="text-sm font-semibold text-[#1F1F1F]">{node}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.25} y={30}>
              <div className="img-shine rounded-2xl mb-10 shadow-sm">
                <img
                  src={program.image}
                  alt={program.title}
                  className="w-full h-64 lg:h-80 object-cover rounded-2xl"
                />
              </div>
            </Reveal>

            {/* What you'll learn */}
            <div className="mb-10">
              <Reveal>
                <h2 className="font-heading font-bold text-2xl text-[#0F1F3D] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" style={{ color: program.accent }} />
                  What You'll Learn
                </h2>
              </Reveal>
              <div className="flex flex-wrap gap-2">
                {program.topics.map((topic, i) => (
                  <Reveal key={topic} delay={i * 0.06} y={12}>
                    <span
                      className="inline-block text-sm font-semibold px-3.5 py-1.5 rounded-full hover:scale-105 transition-transform duration-200"
                      style={{ backgroundColor: program.bg, color: program.accent }}
                    >
                      {topic}
                    </span>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Curriculum */}
            <div className="mb-10">
              <Reveal>
                <h2 className="font-heading font-bold text-2xl text-[#0F1F3D] mb-5">Curriculum Overview</h2>
              </Reveal>
              <div className="grid sm:grid-cols-2 gap-4">
                {program.modules.map((mod, i) => (
                  <Reveal key={mod.title} delay={i * 0.08} y={26}>
                    <SpotlightCard
                      spotlightColor={program.accent}
                      tilt
                      className="group h-full rounded-xl border border-[#E1E7EF] bg-white p-5 hover:shadow-xl"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                          style={{ backgroundColor: program.bg, color: program.accent }}
                        >
                          {i + 1}
                        </span>
                        <h3 className="font-heading font-bold text-sm text-[#1F1F1F]">{mod.title}</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {mod.points.map(point => (
                          <li key={point} className="flex items-start gap-2 text-sm text-[#5A6A85]">
                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: program.accent }} />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </SpotlightCard>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Who should enroll */}
            <div>
              <Reveal>
                <h2 className="font-heading font-bold text-2xl text-[#0F1F3D] mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: program.accent }} />
                  Who Should Enroll
                </h2>
              </Reveal>
              <ul className="space-y-2">
                {program.idealFor.map((item, i) => (
                  <Reveal key={item} delay={i * 0.08} x={-24} y={0}>
                    <li className="flex items-start gap-2 text-sm text-[#5A6A85] bg-white rounded-lg border border-[#E1E7EF] p-3 hover:shadow-sm transition-shadow duration-200">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: program.accent }} />
                      {item}
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>

          {/* Lead form column — 40% */}
          <div className="lg:sticky lg:top-24">
            <Reveal delay={0.2} x={40} y={0}>
              <div className="relative">
                <div
                  className="breathe-glow absolute -inset-3 rounded-3xl blur-2xl pointer-events-none"
                  style={{ backgroundColor: `${program.accent}30` }}
                />
                <div className="relative">
                  {submitted ? (
                    <div
                      data-testid="lead-unlocked-panel"
                      className="bg-white rounded-2xl border border-[#E1E7EF] shadow-sm p-6 lg:p-7 text-center"
                    >
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: program.bg }}>
                        <CheckCircle2 className="w-7 h-7" style={{ color: program.accent }} />
                      </div>
                      <h3 className="font-heading font-bold text-lg text-[#1F1F1F]">
                        Thanks{leadName ? `, ${leadName}` : ''}!
                      </h3>
                      <p className="font-body text-sm text-[#585858] mt-2">
                        Our counsellor will reach out to you shortly about the {program.title} program.
                      </p>
                    </div>
                  ) : (
                    <ProgramLeadForm program={program} onSuccess={handleLeadSubmitted} />
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-[#E1E7EF] py-6">
        <Reveal className="max-w-7xl mx-auto px-6 lg:px-12 text-center text-xs text-[#9AA5B5]">
          © {new Date().getFullYear()} A7 Globetech. All rights reserved.
        </Reveal>
      </footer>

      {brochureModalOpen && (
        <BrochureDownloadModal
          program={program}
          onClose={() => setBrochureModalOpen(false)}
        />
      )}

      {popupOpen && !submitted && (
        <ProgramLeadPopup
          program={program}
          onClose={() => setPopupOpen(false)}
          onSuccess={(submittedForm) => {
            setPopupOpen(false);
            handleLeadSubmitted(submittedForm);
          }}
        />
      )}

      <WhatsAppWidget />
    </div>
  );
}
